import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, ClipboardCheck, Calendar as CalendarIcon, Factory, User } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { watchOrderEmail } from "@/lib/orderEmail";
import { formatMoney, docCurrency } from "@/lib/currency";
import { PartialDeliveryList } from "@/components/PartialDeliveryEditor";

/**
 * Production Approvals — Production team accepts SOs raised by Marketing.
 * Requires role: admin / planner / production_manager (also enforced backend-side).
 * Approving auto-creates Work Orders and moves the Transfer Order to `in_production`.
 */
export default function ProductionApprovalsPage() {
  const { user } = useAuth();
  const [sos, setSos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [lineDates, setLineDates] = useState({});  // Phase 26 — {lineIdx: "YYYY-MM-DD"}
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ production_date: "", estimated_completion_date: "", notes: "" });
  const [dialogOpen, setDialogOpen] = useState(false);

  const custMap = useMemo(() => Object.fromEntries(customers.map((c) => [c.id, c])), [customers]);
  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);

  const load = async () => {
    const [s, c, i] = await Promise.all([
      api.get("/sales-orders"),
      api.get("/customers"),
      api.get("/items"),
    ]);
    setCustomers(c.data);
    setItems(i.data);
    const pending = (s.data || []).filter((x) => x.status === "order_raised");
    setSos(pending);
    if (selected) {
      const found = pending.find((x) => x.id === selected.id);
      setSelected(found || null);
      if (found) hydrateLineDates(found);
    }
  };

  const hydrateLineDates = (so) => {
    const map = {};
    (so.lines || []).forEach((l, idx) => {
      // Slice ISO string directly to avoid timezone drift (Date parsing shifts by TZ offset)
      map[idx] = l.delivery_date ? String(l.delivery_date).slice(0, 10) : "";
    });
    setLineDates(map);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (selected) hydrateLineDates(selected); }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const setLineDate = (idx, v) => setLineDates((m) => ({ ...m, [idx]: v }));

  const allDatesFilled = selected && selected.lines?.length > 0 &&
    selected.lines.every((_, idx) => !!lineDates[idx]);

  const openApprove = () => {
    if (!selected) return;
    if (!allDatesFilled) {
      return toast.error("Please enter delivery date for all line items before approving.");
    }
    const today = new Date().toISOString().slice(0, 10);
    setForm({
      production_date: today,
      estimated_completion_date: "",
      notes: "",
    });
    setDialogOpen(true);
  };

  const submitApproval = async () => {
    if (!form.production_date) return toast.error("Production Date is required");
    if (form.estimated_completion_date && new Date(form.estimated_completion_date) < new Date(form.production_date)) {
      return toast.error("Estimated completion cannot be before production date");
    }
    if (!allDatesFilled) {
      return toast.error("Please enter delivery date for all line items before approving.");
    }
    setBusy(true);
    try {
      // First, persist per-line delivery dates onto the SO
      const updatedLines = selected.lines.map((l, idx) => ({
        ...l,
        delivery_date: new Date(lineDates[idx]).toISOString(),
      }));
      await api.patch(`/sales-orders/${selected.id}`, { lines: updatedLines });

      // Then trigger production approval
      const body = {
        production_date: new Date(form.production_date).toISOString(),
        notes: form.notes || "",
      };
      if (form.estimated_completion_date) {
        body.estimated_completion_date = new Date(form.estimated_completion_date).toISOString();
      }
      const { data } = await api.post(`/sales-orders/${selected.id}/approve-production`, body);
      toast.success(`${selected.code} approved · ${data.work_orders_created} Work Order(s) created`);
      // Sales Order raised → its email is sent in the background; toast the result.
      if (data.customer_sales_order?.id) {
        watchOrderEmail(`/customer-sales-orders/${data.customer_sales_order.id}`,
          `Sales Order ${data.customer_sales_order.code}`, data.sales_order_email, user?.email);
      }

      // Auto-download the confirmed Sales Order PDF — now carrying the locked-in
      // per-line delivery dates and Production Date — headed "SALES ORDER".
      try {
        const res = await api.get(`/sales-orders/${selected.id}/pdf`, {
          params: { sales_order: true },
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a"); a.href = url; a.download = `SalesOrder-${selected.code}.pdf`;
        document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      } catch {
        toast.error("Approved, but Sales Order PDF download failed");
      }

      setDialogOpen(false);
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Approval failed");
    } finally {
      setBusy(false);
    }
  };

  const soTotal = (so) => (so.lines || []).reduce((s, l) => s + (l.qty || 0) * (l.price || 0), 0);

  return (
    <div className="space-y-6" data-testid="production-approvals-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Production</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Production Approvals
          </h1>
          <p className="text-sm text-slate-500 mt-1">Every submitted Transfer Order lands here. Attach the Production Date to approve and auto-create a Work Order.</p>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="pending-count">
          <ClipboardCheck className="w-4 h-4" />
          <span className="font-semibold mono">{sos.length}</span>
          <span>pending {sos.length === 1 ? "order" : "orders"}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* List */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">
            Order Raised · Awaiting Dates
          </div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto scroll-thin">
            {sos.length === 0 && (
              <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="empty-approvals">
                No transfer orders pending production approval.
              </div>
            )}
            {sos.map((so) => (
              <button
                key={so.id}
                onClick={() => setSelected(so)}
                data-testid={`so-approval-row-${so.code}`}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${selected?.id === so.id ? "bg-blue-50/60" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <div className="mono text-xs text-slate-500 uppercase tracking-[0.14em]">{so.code}</div>
                  <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-orange-50 text-orange-700">Order Raised</span>
                </div>
                <div className="mt-1 text-sm font-medium text-slate-800">
                  {custMap[so.customer_id]?.code} — {custMap[so.customer_id]?.name}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {so.lines.length} line(s) · {formatMoney(soTotal(so), docCurrency(so))} · by <span className="mono">{so.created_by}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[60vh]" data-testid="approval-detail">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 gap-2">
              <Factory className="w-10 h-10 text-slate-300" />
              Pick an order raised by Marketing to review and approve.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{selected.code}</div>
                  <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>
                    {custMap[selected.customer_id]?.name || "—"}
                  </h2>
                  <div className="text-xs text-slate-500 mt-1">
                    PO ref: <span className="mono">{selected.po_reference || "—"}</span>
                  </div>
                </div>
                <Button
                  onClick={openApprove}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="approve-production-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve & Create Work Order
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4 text-xs">
                <div className="bg-slate-50 rounded-md p-3 border border-slate-100">
                  <div className="font-semibold text-slate-700 mb-1 flex items-center gap-1"><User className="w-3.5 h-3.5" /> Raised By</div>
                  <div className="mono text-slate-700">{selected.created_by || "—"}</div>
                  <div className="text-slate-500 mt-1">On {selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"}</div>
                </div>
                <div className="bg-amber-50 rounded-md p-3 border border-amber-100">
                  <div className="font-semibold text-amber-800 mb-1 flex items-center gap-1"><CalendarIcon className="w-3.5 h-3.5" /> Line-level Delivery Dates</div>
                  <div className="text-amber-800">
                    Each line item below carries its own delivery date. Fill every date before approval.
                  </div>
                  <div className={`mt-1 text-[11px] mono ${allDatesFilled ? "text-emerald-700" : "text-rose-700"}`} data-testid="delivery-dates-status">
                    {selected.lines?.filter((_, idx) => !!lineDates[idx]).length || 0} / {selected.lines?.length || 0} dates set
                  </div>
                </div>
              </div>

              {selected.notes && (
                <div className="text-sm text-slate-600 border-l-2 border-blue-300 pl-3 italic whitespace-pre-wrap">{selected.notes}</div>
              )}

              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Lines</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="approval-lines-table">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                        <th className="py-2 px-3">Code</th>
                        <th className="py-2 px-3">Item</th>
                        <th className="py-2 px-3">HSN</th>
                        <th className="py-2 px-3">Qty</th>
                        <th className="py-2 px-3">Rate</th>
                        <th className="py-2 px-3">Amount</th>
                        <th className="py-2 px-3">Est Delivery Date *</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.lines.map((l, idx) => {
                        const dateSet = !!lineDates[idx];
                        return (
                          <tr key={idx} className="border-t border-slate-100" data-testid={`approval-line-row-${idx}`}>
                            <td className="py-2 px-3 mono">{itemMap[l.item_id]?.code}</td>
                            <td className="py-2 px-3 text-slate-700">{itemMap[l.item_id]?.name}</td>
                            <td className="py-2 px-3 mono">{l.hsn_code || "—"}</td>
                            <td className="py-2 px-3 mono">{l.qty} {l.uom || "PCS"}</td>
                            <td className="py-2 px-3 mono">{formatMoney(Number(l.price), docCurrency(selected))}</td>
                            <td className="py-2 px-3 mono font-semibold">{formatMoney((l.qty * l.price), docCurrency(selected))}</td>
                            <td className="py-2 px-3">
                              <Input
                                type="date"
                                value={lineDates[idx] || ""}
                                onChange={(e) => setLineDate(idx, e.target.value)}
                                disabled={!!l.partial_delivery}
                                title={l.partial_delivery ? "Partial Delivery — set by the last delivery date on the Transfer Order" : undefined}
                                data-testid={`line-delivery-date-${idx}`}
                                className={`h-8 ${dateSet ? "" : "border-rose-300 ring-1 ring-rose-100"}`}
                              />
                              <PartialDeliveryList line={l} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="text-xs text-slate-500 border-t border-slate-100 pt-3">
                Approving this order attaches the Production Date, auto-generates one or more Work Orders inheriting the
                Part Number, Quantity, and Transfer Order ID, and moves the Transfer Order to <span className="mono">in_production</span>.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Approval Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve for Production — {selected?.code}</DialogTitle>
            <DialogDescription>Attach the Production Date. A Work Order will be created automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="prod_date">Production Date *</Label>
              <Input
                id="prod_date"
                type="date"
                value={form.production_date}
                onChange={(e) => setForm({ ...form, production_date: e.target.value })}
                data-testid="approve-production-date"
              />
            </div>
            <div>
              <Label htmlFor="ecd">Estimated Completion Date <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Input
                id="ecd"
                type="date"
                value={form.estimated_completion_date}
                onChange={(e) => setForm({ ...form, estimated_completion_date: e.target.value })}
                data-testid="approve-est-completion"
              />
            </div>
            <div>
              <Label htmlFor="approval-notes">Notes (optional)</Label>
              <Textarea
                id="approval-notes"
                rows={2}
                placeholder="Batch preference, machine allocation, priority…"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="approve-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              onClick={submitApproval}
              disabled={busy}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="approve-dialog-submit"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              {busy ? "Approving…" : "Approve & Create Work Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
